import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import styled, { css } from 'styled-components'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'

// Components
import AppLayout from '../../AppLayout'
import { GridRow, GridCol } from '../grid'
import { IFilterBundle, IFilterDescriptor } from '../filter/FilterPanel'
import StickyFilterPanel from '../filter/StickyFilterPanel'
import { FilterChipDescriptor } from '../filter/FilterChips'
import { useMemberFilters } from '../../hooks/useMemberFilters'
import StatusTag from '../tag/StatusTag'
import Icon from '../icon/Icon'
import Button from '../Button'
import JobTag from '../tag/JobTag'
import { Row, RowAligned } from '../layout/LayoutAtoms'
import Text, { MarkDownText } from '../Text'
import UserDetails from '../card/UserDetails'
import { IMembers, StyledGroupedAvatars } from '../Avatar'
import Table, { MainRow } from '../layout/Table'
import EvaluationModal from '../EvaluationModal'
import ContentModal from '../layout/ContentModal'
import { SelectContact } from '../onboarding/atoms/SelectContact'
import Checkbox from '../formControl/Checkbox'
import FormFinish from '../FormFinish'
import { ProgressBar } from '../ProgressBar'
import Input from '../formControl/Input'
import DialogScreen from '../DialogScreen'
import { contextButton } from '../ContextButton'
import StartConfirmation from '../evaluations/StartConfirmation'
import CircularProgress from '../CircularProgress'
import AssessmentsProgress from '../evaluations/AssessmentsProgress'
import Loader from '../Loader'
import Insights from '../insights/Insights'
import DeltaPill from '../insights/DeltaPill'
import KPIStrip from '../insights/KPIStrip'
import CultureStrip from '../insights/CultureStrip'
import TermFilter, { newestTermsFirst } from '../filter/TermFilter'
import GoalCount, { CheckIcon } from '../GoalCount'
import {
  ValueChip,
  ValueWrapper,
  ChipWithAvatar,
  StyledGroupAvatars,
} from '../EvaluationHeader'
import Switch from '../formControl/Switch'

// Styles
import { mqFrom, mqTo } from '../../styles/mediaQueries'
import { fastTransition } from '../../styles/effects'
import { FontWeight } from '../../common/models/typography'

// Utils
import {
  getProfileBreadcrumbs,
  IHistoryState,
  scrollToPinnedTabs,
} from '../../utils/navHelper'
import { useAppState, useAppDispatch } from '../../context/AppContext'
import getCompanyTermEvaluationStateService from '../../context/services/evaluations/getCompanyTermEvaluationState.service'
import addEvaluationAssessorsService from '../../context/services/evaluations/addEvaluationAssessors.service'
import loadCompanyMetadataService from '../../context/services/loadCompanyMetadata.service'
import removeEvaluationAssessorsService from '../../context/services/evaluations/removeEvaluationAssessors.service'
import reopenEvaluationAssessmentService from '../../context/services/evaluations/reopenEvaluationAssessment.service'
import finalizeEvaluationPlanningService from '../../context/services/evaluations/finalizeEvaluationPlanning.service'
import closeEvaluationsService from '../../context/services/evaluations/closeEvaluations.service'
import sendSuggestRequestsService from '../../context/services/requests/sendSuggestRequests.service'
import editEvaluationNameService from '../../context/services/evaluations/editEvaluationName.service'
import editHideAssessmentsService from '../../context/services/evaluations/editHideAssessments.service'
import { notEmpty } from '../../utils/typeGuards'
import { memberHasNoSkills } from '../../common/helpers/member.helper'
import {
  getMemberRole,
  getMembersVisibleToLead,
  isLeadOfTeam,
  isMemberBuddyOfAnotherMember,
  isMemberStrictLeadOfMember,
} from '../../common/helpers/isLeadOf'
import termsHelper from '../../common/helpers/terms.helper'
import { getFilterEmptyState } from '../../common/helpers/filterEmptyState.helper'
import { formatDate } from '../../utils/dateHelper'
import {
  useEvaluationStatistics,
  useEvaluationResultAverages,
} from '../../hooks/useEvaluationStatistics'
import { usePdfExport } from '../../hooks/usePdfExport'
import {
  calculateAverageExpectationValues,
  getAvgGoalEvaluations,
  calculatePercentageAlignmentValue,
  getClosestStep,
  getCultureScores,
  getSkillScores,
} from '../../utils/evaluationHelpers'
import { getAllowedContributors } from '../../common/helpers/company.helper'
import { adaptBackendCompanyToFrontendCompany } from '../../context/services/loadCompany.service'

// Types
import { IconType } from '../icon/IconType'
import { ITermState, ITermStateMember } from '../../common/models/Term'
import { PdpStatus } from '../../common/models/Pdp'
import { TermActions } from '../../common/helpers/termConfigurations.helper'
import {
  EvaluationStatus,
  IEvaluationAssessor,
  IEvaluationAssessorsUpdateRequest,
  SingleEvaluationStatus,
  IEvaluationState,
  IEvaluationPure,
  EvaluationAssessmentType,
  ISingleEvaluationPure,
} from '../../common/models/Evaluation'
import {
  ICompanyMember,
  ICompanyTerm,
  EvaluationsRevealOptions,
  IRevealAssessmentsConfig,
} from '../../common/models/Company'
import { ICompanyMetadataMemberPure } from '../../common/models/CompanyMetadata'
import { PdfExportType } from '../../common/models/PdfExport'

interface IOpenAddModal {
  member: ICompanyMember
  type: EvaluationAssessmentType
  assessors?: IEvaluationAssessor[]
}

const TermEvaluationsProgress: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const historyState = history.location.state as IHistoryState
  const preSelectedTermNumber = historyState?.termNumber
  const appDispatch = useAppDispatch()
  const { company, user, userAuthToken, theme } = useAppState()
  const { exportPdf, isExporting } = usePdfExport()
  const teamFromNav =
    historyState &&
    historyState.teamId &&
    company?.teams.find((tm) => tm._id === historyState.teamId)
  const isAdmin = user?.asCompanyMember?.isAdmin

  const openEvals = company?.termsInformation.terms.filter(
    (term) => term.evaluationPhaseOpened && !term.evaluationPhaseClosed
  )
  const resultEvals = company?.termsInformation.terms.filter(
    (term) => term.evaluationPlanningComplete
  )

  const [selectedMember, setSelectedMember] = useState<ICompanyMember>()
  const [
    selectedEvaluationState,
    setSelectedEvaluationState,
  ] = useState<ITermStateMember>()
  // Stable array reference so EvaluationModal doesn't rebuild its state (and
  // reset the user's filter/toggle choices) on every unrelated re-render — e.g.
  // the 30s auth-token poll that re-renders all useAppState consumers.
  const modalEvaluations = useMemo(
    () => (selectedEvaluationState ? [selectedEvaluationState.evaluation] : []),
    [selectedEvaluationState]
  )
  const [evaluationStates, setEvaluationStates] = useState<ITermStateMember[]>()
  const [termState, setTermState] = useState<ITermState>()
  const [prevEvaluationStates, setPrevEvaluationStates] = useState<
    ITermStateMember[]
  >()
  const [prevTermState, setPrevTermState] = useState<ITermState>()
  // The immediately-preceding evaluated term, fetched independently of the
  // compare-to filter so the evaluation modal can always show a delta vs the
  // member's previous result.
  const [autoPrevTermState, setAutoPrevTermState] = useState<ITermState>()
  const [openAddModal, setOpenAddModal] = useState<IOpenAddModal>()
  const [disableButton, setDisableButton] = useState<boolean>(false)
  const [termName, setTermName] = useState<string>('')
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false)
  const [suggestData, setSuggestDate] = useState<ICompanyMetadataMemberPure[]>()
  const [openStatistics, setOpenStatistics] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'progress' | 'results'>('progress')
  const [selectedTerm, setSelectedTerm] = useState<ICompanyTerm>()
  const [members, setMembers] = useState<ICompanyMember[]>()
  // Compare to term for trends (used only in results tab)
  const [compareTo, setCompareTo] = useState<ICompanyTerm>()
  // Simplified (heatmap) vs detailed (scatter) view of the 9-grid
  const [nineGridView, setNineGridView] = useState<'heatmap' | 'scatter'>(
    'scatter'
  )
  const [isLoading, setIsLoading] = useState<boolean>()
  // Used on results tab to show/hide own results by avatar
  const [showSelfResults, setShowSelfResults] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const noCultureRanks = (company?.cultureRanks.length ?? 0) <= 1

  const filterScope = useMemo(() => {
    if (!user?.asCompanyMember || !company || isAdmin) return undefined
    const viewer = user.asCompanyMember
    return {
      members: getMembersVisibleToLead(viewer, company),
      teamIds: company.teams
        .filter((team) => isLeadOfTeam(viewer, team._id))
        .map((team) => team._id),
    }
  }, [user?.asCompanyMember, company, isAdmin])

  const filters = useMemberFilters(company, {
    team: true,
    job: true,
    culturalPhase: !noCultureRanks,
    initial: teamFromNav ? { team: [teamFromNav._id] } : undefined,
    scope: filterScope,
  })
  const { team: teamFilter } = filters.values
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  useMemo(() => {
    if (!company || !user?.asCompanyMember) {
      return
    }
    setMembers(filterScope?.members ?? company.members ?? [])
  }, [filterScope, company, user?.asCompanyMember])

  useEffect(() => {
    if (!preSelectedTermNumber) {
      if (!company) return
      // By default selectedTerm is always the latest term with evaluationPhaseOpened
      if (openEvals && openEvals.length) {
        setSelectedTerm(openEvals[openEvals.length - 1])
      } else if (resultEvals && resultEvals.length) {
        setActiveTab('results')
        setSelectedTerm(resultEvals[resultEvals.length - 1])
      }
    } else {
      const preTerm = company?.termsInformation.terms.find(
        (term) => term.termNumber === Number(preSelectedTermNumber)
      )
      if (preTerm) {
        setSelectedTerm(preTerm)
        if (
          !openEvals?.find((term) => term.termNumber === preTerm.termNumber)
        ) {
          setActiveTab('results')
        }
      }
    }
  }, [preSelectedTermNumber, company])

  useEffect(() => {
    if (!user?.asCompanyMember) return
    // team leads never have access to progress tab
    if (!user.asCompanyMember.isAdmin) {
      setActiveTab('results')
    }
  }, [user?.asCompanyMember])

  // Sort evaluation states to activity filter,
  const views = [
    {
      tabLabel: t('general.forms.allActivity'),
      members: members || [],
    },
    {
      tabLabel: t('termEvaluationsProgress.openEvaluations'),
      color: theme.colors.primary,
      members:
        members?.filter((companyMember) =>
          evaluationStates?.find(
            (member) =>
              member.evaluation.status === EvaluationStatus.NOT_STARTED &&
              companyMember._id === member.evaluation.memberId &&
              !member.evaluation.skipped
          )
        ) || [],
    },
    {
      tabLabel: t('termEvaluationsProgress.inProgress'),
      color: theme.colors.primaryMild,
      members:
        members?.filter((companyMember) =>
          evaluationStates?.find(
            (member) =>
              companyMember._id === member.evaluation.memberId &&
              member.evaluation.status === EvaluationStatus.IN_PROGRESS &&
              !member.evaluation.skipped
          )
        ) || [],
    },
    {
      tabLabel: t('general.states.done'),
      color: theme.colors.tertiary,
      members:
        members?.filter((companyMember) =>
          evaluationStates?.find(
            (member) =>
              companyMember._id === member.evaluation.memberId &&
              member.evaluation.status === EvaluationStatus.DONE &&
              !member.evaluation.skipped
          )
        ) || [],
    },
    {
      tabLabel: t('termEvaluationsProgress.withoutEvaluations'),
      color: theme.colors.secondary,
      members:
        members?.filter(
          (member) =>
            !evaluationStates?.find(
              (evalMember) => member._id === evalMember.evaluation.memberId
            ) ||
            evaluationStates.find(
              (state) =>
                member._id === state.evaluation.memberId &&
                state.evaluation.skipped
            )
        ) || [],
    },
  ]

  const visibleMembers = useMemo(() => {
    const noPrevSelected = compareTo && statusFilter.includes('noPrevious')
    return (members || [])
      .filter(filters.matches)
      .filter((m) => {
        if (statusFilter.length === 0) return true
        const indexFilters = statusFilter.filter((v) => v !== 'noPrevious')
        const matchesIndex =
          indexFilters.length === 0
            ? true
            : indexFilters.some((idx) =>
                views[Number(idx)]?.members.some((vm) => vm._id === m._id)
              )
        if (!noPrevSelected) return matchesIndex
        const hasPrev = prevEvaluationStates?.some(
          (es) => es.evaluation.memberId === m._id
        )
        return matchesIndex && !hasPrev
      })
      .filter((m) => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [
    members,
    filters.matches,
    statusFilter,
    searchTerm,
    views,
    compareTo,
    prevEvaluationStates,
  ])

  const visibleMemberIds = useMemo(
    () => new Set(visibleMembers.map((m) => m._id)),
    [visibleMembers]
  )

  const visibleEvaluationStates = useMemo(
    () =>
      (evaluationStates || []).filter((es) =>
        visibleMemberIds.has(es.evaluation.memberId)
      ),
    [evaluationStates, visibleMemberIds]
  )

  const statistics = useEvaluationStatistics(visibleEvaluationStates)

  const visiblePrevEvaluationStates = useMemo(
    () =>
      (prevEvaluationStates || []).filter((es) =>
        visibleMemberIds.has(es.evaluation.memberId)
      ),
    [prevEvaluationStates, visibleMemberIds]
  )

  const resultAverages = useEvaluationResultAverages(
    visibleEvaluationStates,
    termState,
    visibleMembers,
    company
  )
  const prevResultAverages = useEvaluationResultAverages(
    visiblePrevEvaluationStates,
    prevTermState,
    visibleMembers,
    company
  )

  const activeMemberIds = useMemo(
    () => visibleMembers?.map((m) => m._id) ?? [],
    [visibleMembers]
  )

  const breadCrumb = getProfileBreadcrumbs(
    company,
    history.location.state as IHistoryState,
    getMemberRole(user?.asCompanyMember)
  )

  const termReviewStepDate =
    company &&
    termsHelper.getDateOfTermEvent(
      company,
      Number(selectedTerm?.termNumber),
      TermActions.TERM_REVIEW
    )
  const termEndStepDate =
    company &&
    termsHelper.getDateOfTermEvent(
      company,
      Number(selectedTerm?.termNumber),
      TermActions.FINALISE_TERM
    )
  const phaseOpened = selectedTerm?.evaluationPhaseOpened
  const planningComplete = selectedTerm?.evaluationPlanningComplete
  const closed = selectedTerm?.evaluationPhaseClosed
  const startTermDate = selectedTerm && new Date(selectedTerm.dateCreated)
  const evalsNotStarted = phaseOpened && !planningComplete
  const termOver = new Date().getTime() > Number(termEndStepDate)
  const evalsActive = phaseOpened && planningComplete && !termOver
  const revealResultsSelf =
    company?.settings?.evaluations?.revealAssessmentsSelf
  const revealResultsVisitors =
    company?.settings?.evaluations?.revealAssessmentsVisitor
  const overNotStarted = termOver && !planningComplete
  const termDate = termReviewStepDate && new Date(termReviewStepDate)
  const termEndDate = termEndStepDate && new Date(termEndStepDate)
  const planningDate =
    selectedTerm?.evaluationPlanningCompleteDate &&
    new Date(selectedTerm?.evaluationPlanningCompleteDate)
  const closeDate =
    selectedTerm?.evaluationPhaseClosedDate &&
    new Date(selectedTerm?.evaluationPhaseClosedDate)
  const evalsNotOpenYet =
    new Date().getTime() < Number(termReviewStepDate) && !phaseOpened
  const resultsViewTermOpen = activeTab === 'results' && !closed

  const loadStates = async (resetMember?: ICompanyMember) => {
    if (!company || !selectedTerm?.termNumber) return
    setIsLoading(true)
    const state = await getCompanyTermEvaluationStateService(
      selectedTerm?.termNumber.toString(),
      userAuthToken || ''
    )
    setTermState(state)
    setEvaluationStates(state.memberStates)
    setIsLoading(false)
    if (resetMember) {
      setSelectedEvaluationState(
        state.memberStates?.find(
          (s) => s.evaluation.memberId === resetMember._id
        )
      )
    }
  }

  const addAssessors = async (modalProps: IOpenAddModal) => {
    setDisableButton(true)
    const prevAssessors = evaluationStates?.find(
      (state) => state.evaluation.memberId === modalProps.member._id
    )?.evaluation.assessors
    const requestAssessors: IEvaluationAssessorsUpdateRequest = {
      goalAssessorIds: (modalProps.type === 'goals'
        ? modalProps.assessors || []
        : prevAssessors || []
      )
        .filter((as) => as.goals)
        .map((as) => as.memberId),
      skillAssessorIds: (modalProps.type === 'skills'
        ? modalProps.assessors || []
        : prevAssessors || []
      )
        .filter((as) => as.skills)
        .map((as) => as.memberId),
      cultureAssessorIds: (modalProps.type === 'culture'
        ? modalProps.assessors || []
        : prevAssessors || []
      )
        .filter((as) => as.culture)
        .map((as) => as.memberId),
    }

    const assessorsToRemove = prevAssessors?.filter(
      (as) =>
        as[modalProps.type] &&
        !modalProps.assessors?.some((newAs) => newAs.memberId === as.memberId)
    )

    if (assessorsToRemove?.length && selectedEvaluationState) {
      await removeAssessors(
        selectedEvaluationState.evaluation,
        assessorsToRemove.map((as) => as.memberId),
        modalProps.type
      )
    }

    if (
      Object.values(requestAssessors).some((ids) => ids.length > 0) &&
      selectedEvaluationState
    ) {
      await addEvaluationAssessorsService(
        selectedEvaluationState?.evaluation._id,
        requestAssessors,
        userAuthToken || ''
      )
    }
    setOpenAddModal(undefined)
    setDisableButton(false)
    await loadStates(modalProps?.member)
  }

  const removeAssessors = async (
    evaluation: IEvaluationState | IEvaluationPure,
    assessorIds: string[],
    typeOfAssessor: EvaluationAssessmentType
  ) => {
    const requestAssessors: IEvaluationAssessorsUpdateRequest = {
      goalAssessorIds: typeOfAssessor === 'goals' ? assessorIds : [],
      cultureAssessorIds: typeOfAssessor === 'culture' ? assessorIds : [],
      skillAssessorIds: typeOfAssessor === 'skills' ? assessorIds : [],
    }

    await removeEvaluationAssessorsService(
      evaluation._id,
      requestAssessors,
      userAuthToken || ''
    )
    setOpenAddModal(undefined)
    await loadStates(selectedMember)
  }

  const reopenAssessment = async (
    evaluation: IEvaluationState | IEvaluationPure,
    assessorId: string,
    typeOfAssessor: EvaluationAssessmentType
  ) => {
    const requestAssessors: IEvaluationAssessorsUpdateRequest = {
      goalAssessorIds: typeOfAssessor === 'goals' ? [assessorId] : [],
      cultureAssessorIds: typeOfAssessor === 'culture' ? [assessorId] : [],
      skillAssessorIds: typeOfAssessor === 'skills' ? [assessorId] : [],
    }

    await reopenEvaluationAssessmentService(
      evaluation._id,
      requestAssessors,
      userAuthToken || ''
    )
    await loadStates(selectedMember)
  }

  const finalizePlanning = async () => {
    setDisableButton(true)
    if (selectedTerm?.name !== termName && termName.length > 1) {
      await editEvaluationNameService(
        String(selectedTerm?.termNumber),
        termName,
        userAuthToken || ''
      )
    }
    await finalizeEvaluationPlanningService(
      String(selectedTerm?.termNumber),
      userAuthToken || ''
    )
    setOpenConfirmation(false)
    appDispatch({
      type: 'updateShouldLoadCompany',
      payload: true,
    })
    appDispatch({
      type: 'updateAlertPopup',
      payload: undefined,
    })
    await loadStates()
    setDisableButton(false)
  }

  const closeEvaluations = async () => {
    setDisableButton(true)
    await closeEvaluationsService(
      String(selectedTerm?.termNumber),
      userAuthToken || ''
    )
    appDispatch({
      type: 'updateShouldLoadCompany',
      payload: true,
    })
    appDispatch({
      type: 'updateAlertPopup',
      payload: undefined,
    })
    await loadStates()
    setDisableButton(false)
  }

  const sendSuggestions = async () => {
    setDisableButton(true)
    if (!selectedTerm?.termNumber) return
    await sendSuggestRequestsService(
      selectedTerm?.termNumber,
      userAuthToken || ''
    )
    appDispatch({
      type: 'updateShouldLoadCompany',
      payload: true,
    })
    appDispatch({
      type: 'updateAlertPopup',
      payload: undefined,
    })
    setDisableButton(false)
  }

  const renameEvaluation = async () => {
    setDisableButton(true)
    await editEvaluationNameService(
      String(selectedTerm?.termNumber),
      termName,
      userAuthToken || ''
    )
    appDispatch({
      type: 'updateShouldLoadCompany',
      payload: true,
    })
    setOpenEditModal(false)
    setDisableButton(false)
  }

  // Update term name + get evaluationStates every time other term is selected
  useEffect(() => {
    if (
      !selectedTerm ||
      (evaluationStates &&
        evaluationStates.length > 0 &&
        evaluationStates[0]?.evaluation.termNumber === selectedTerm?.termNumber)
    ) {
      return
    }
    setTermName(selectedTerm.name || '')
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      await loadStates()
    })()
  }, [company, selectedTerm?.termNumber])

  // Fetch prev term states whenever compareTo changes
  useEffect(() => {
    if (!compareTo) {
      setPrevEvaluationStates(undefined)
      setPrevTermState(undefined)
      return
    }
    if (!company) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      try {
        const state = await getCompanyTermEvaluationStateService(
          compareTo.termNumber.toString(),
          userAuthToken || ''
        )
        setPrevTermState(state)
        setPrevEvaluationStates(state.memberStates)
      } catch {
        setPrevTermState(undefined)
        setPrevEvaluationStates(undefined)
      }
    })()
  }, [company, compareTo?.termNumber, userAuthToken])

  // The most recent term before the selected one that was actually evaluated —
  // used as the default "previous" baseline for deltas in the evaluation modal.
  const previousTerm = (company?.termsInformation.terms || [])
    .filter(
      (term) =>
        selectedTerm != null &&
        term.termNumber < selectedTerm.termNumber &&
        term.evaluationPlanningComplete
    )
    .sort((a, b) => b.termNumber - a.termNumber)[0]

  // Fetch the previous term's evaluation state independently of compareTo so
  // the modal can always compare a member's result to their previous one.
  useEffect(() => {
    if (!company || !previousTerm) {
      setAutoPrevTermState(undefined)
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      try {
        const state = await getCompanyTermEvaluationStateService(
          previousTerm.termNumber.toString(),
          userAuthToken || ''
        )
        setAutoPrevTermState(state)
      } catch {
        setAutoPrevTermState(undefined)
      }
    })()
  }, [company, previousTerm?.termNumber, userAuthToken])

  // Clear compareTo when moving away from results tab
  useEffect(() => {
    if (activeTab !== 'results' && compareTo) {
      setCompareTo(undefined)
    }
  }, [activeTab])

  useEffect(() => {
    if (
      !company ||
      !selectedTerm?.termNumber ||
      selectedTerm?.evaluationPhaseClosed ||
      !isAdmin
    ) {
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      const metaData = await loadCompanyMetadataService(
        userAuthToken || '',
        selectedTerm?.termNumber
      )
      if (metaData && metaData.members) {
        setSuggestDate(metaData.members)
      }
    })()
  }, [company, selectedTerm?.termNumber])

  const getMemberStats = (index: number) => {
    return (members || []).filter((member) =>
      views[index].members?.find((tabMember) => member._id === tabMember._id)
    ).length
  }

  const statusOptions = views
    .map((view, index) => ({ view, index }))
    .filter(({ index }) => index !== 0)
    .map(({ view, index }) => ({
      value: String(index),
      label: view.tabLabel,
      disabled: getMemberStats(index) === 0,
    }))

  const termSource = activeTab === 'results' ? resultEvals : openEvals

  const termFilterEl =
    termSource && termSource.length > 0 ? (
      <TermFilter
        selectedTerm={selectedTerm}
        compareTo={activeTab === 'results' ? compareTo : undefined}
        termOptions={newestTermsFirst(termSource)}
        keyOf={(term) => term.termNumber}
        allowCompare={activeTab === 'results'}
        onSelectTerm={(term) => setSelectedTerm(term)}
        onSetCompareTo={setCompareTo}
      />
    ) : null

  const detailed = nineGridView === 'scatter'
  const nineGridToggle = (
    <DetailViewToggle>
      <Text
        textStyle="label"
        color={detailed ? 'primary' : 'textLight'}
        tag="span"
      >
        {t('insights.nineGrid.toggleScatter')}
      </Text>
      <StyledSwitch
        isOn={detailed}
        ariaLabel={t('insights.nineGrid.toggleScatter')}
        onToggle={() => setNineGridView(detailed ? 'heatmap' : 'scatter')}
      />
    </DetailViewToggle>
  )

  const progressDescriptors = filters.descriptors.filter((d) => d.key !== 'job')
  const progressChips = filters.chips.filter(
    (c) => !c.key.startsWith('job-') && !c.key.startsWith('level-')
  )

  const visibleFilters: IFilterBundle =
    activeTab === 'results'
      ? filters
      : {
          descriptors: progressDescriptors,
          chips: progressChips,
          hasActiveFilters: teamFilter.length > 0,
          clearAll: () => filters.setters.setTeamFilter([]),
        }

  const extraChips: FilterChipDescriptor[] = statusFilter
    .filter((idx) => idx !== 'noPrevious')
    .map((idx) => ({
      key: `status-${idx}`,
      label: views[Number(idx)]?.tabLabel || idx,
      onRemove: () => setStatusFilter((prev) => prev.filter((v) => v !== idx)),
    }))
    .concat(
      statusFilter.includes('noPrevious') && compareTo
        ? [
            {
              key: 'status-noPrevious',
              label: t('termEvaluationsProgress.noPrevious'),
              onRemove: () =>
                setStatusFilter((prev) =>
                  prev.filter((v) => v !== 'noPrevious')
                ),
            },
          ]
        : []
    )
    .concat(
      compareTo && activeTab === 'results'
        ? [
            {
              key: 'compare-to',
              label: `${t('trends.compareTo')}: ${compareTo.name}`,
              onRemove: () => setCompareTo(undefined),
            },
          ]
        : []
    )

  // Results are only exportable once the term is closed — until then the tab
  // itself flags them as provisional (see resultsViewTermOpen). Compare mode has
  // no document of its own, so the button steps aside while it's on.
  const canExportResults =
    !!selectedTerm && !!closed && !compareTo && visibleMembers.length > 0

  // Exports exactly the cohort on screen, so the document's KPIs match the tab's.
  // The active filter labels ride along and are stamped on the cover, so a
  // filtered export can't be mistaken for a whole-company one.
  const exportResults = async () => {
    if (!selectedTerm) return
    try {
      await exportPdf({
        exportType: PdfExportType.TERM_RESULTS,
        params: {
          termNumber: selectedTerm.termNumber,
          memberIds: visibleMembers.map((member) => member._id),
          filterLabels: [
            ...filters.chips.map((chip) => chip.label),
            ...extraChips.map((chip) => chip.label),
            ...(searchTerm ? [`"${searchTerm}"`] : []),
          ],
        },
      })
    } catch {
      // exportPdf already surfaces the failure in the opened tab.
    }
  }

  const resultStatusOptions = [
    ...statusOptions,
    ...(compareTo
      ? [
          {
            value: 'noPrevious',
            label: t('termEvaluationsProgress.noPrevious'),
            disabled: false,
          },
        ]
      : []),
  ]

  const extraFilters: IFilterDescriptor[] =
    activeTab === 'progress'
      ? [
          {
            key: 'status',
            icon: 'Goals',
            label: t('general.status'),
            config: { mode: 'multi' },
            options: statusOptions,
            value: statusFilter,
            onChange: setStatusFilter,
          },
        ]
      : [
          {
            key: 'status',
            icon: 'Goals',
            label: t('general.status'),
            config: { mode: 'multi' },
            options: resultStatusOptions,
            value: statusFilter,
            onChange: setStatusFilter,
          },
        ]

  const extraHasActive = statusFilter.length > 0 || !!compareTo
  const clearExtraFilters = () => {
    setStatusFilter([])
    setCompareTo(undefined)
  }
  const clearAllFiltersAndSearch = () => {
    filters.clearAll()
    clearExtraFilters()
    setSearchTerm('')
  }

  const countLabel = `${visibleMembers.length} ${t('general.users.member', {
    count: visibleMembers.length,
  }).toLowerCase()}`

  const noEvaluationTag = (
    member: ICompanyMember,
    type: EvaluationAssessmentType,
    noFinalScore?: boolean
  ) => (
    <HoverContainer
      hoverText={t(
        noFinalScore
          ? 'evaluationHeader.noFinalScoreExplanation'
          : type === 'skills'
          ? 'termEvaluationsProgress.noSkillsHover'
          : 'termEvaluationsProgress.noGoalsHover',
        {
          name: member.firstName,
        }
      )}
      content={
        <StyledTag
          caption={
            <>
              <Text marginRight={8} tag="span" textStyle="caption">
                {t(
                  noFinalScore
                    ? 'general.states.noFinalScore'
                    : type === 'skills'
                    ? 'termEvaluationsProgress.noSkills'
                    : 'progress.noGoals'
                )}
              </Text>
              <Icon type="Info" color={theme.colors.primary} size={16} />
            </>
          }
          color="textLightest"
        />
      }
    />
  )

  const resultsView = (
    items: Array<{ label: string; value: number }>,
    avg: number,
    self: {
      as?: ISingleEvaluationPure
      member: ICompanyMember
      type: 'skills' | 'culture'
    }
  ) => {
    const avgRounded = Math.round(avg)
    // self avg skills
    const selfAvgSkills =
      showSelfResults &&
      self.as &&
      self.type === 'skills' &&
      self.as.skillsSubmitted &&
      Math.round(
        self.as.skills.reduce((acc, skill) => {
          if (!skill.fields.cannotScoreNow) {
            return acc + skill.fields.expectationValue
          }
          return acc
        }, 0) / self.as.skills.filter((s) => !s.fields.cannotScoreNow).length
      )
    // self avg culture
    const selfAvgCulture =
      showSelfResults &&
      self.as &&
      self.type === 'culture' &&
      self.as.cultureSubmitted &&
      Math.round(
        self.as.culture.reduce((acc, c) => acc + c.alignmentValue, 0) /
          self.as.culture.length
      )

    const closestStep = getClosestStep(items, avgRounded)

    return (
      <ResultTag
        color="textLightest"
        caption={
          <>
            <StyledValueWrapper>
              {items.map((value, index) => (
                <ChipWithAvatar
                  key={`chip-${value.label}-${index}`}
                  width={(value as any).percentage}
                >
                  {((selfAvgSkills &&
                    value.value ===
                      getClosestStep(items, selfAvgSkills).value) ||
                    (selfAvgCulture &&
                      value.value ===
                        getClosestStep(items, selfAvgCulture).value)) && (
                    <StyledGroupAvatars
                      members={[
                        {
                          name: self.member.name,
                          avatar: self.member.picture,
                        },
                      ]}
                    />
                  )}
                  <ValueChip
                    key={index}
                    color={
                      value.value <= closestStep.value ? 'primary' : 'lightest'
                    }
                  />
                </ChipWithAvatar>
              ))}
            </StyledValueWrapper>
            <StyledTextResult>
              <Text textStyle="captionHeavy" marginRight={2}>
                {closestStep.label}
              </Text>
              <Text color="primary" textStyle="caption">{`(${avg.toFixed(
                0
              )}/100)`}</Text>
            </StyledTextResult>
          </>
        }
      />
    )
  }

  const assessorElements = (
    member: ICompanyMember,
    type: EvaluationAssessmentType,
    assessors?: IEvaluationAssessor[]
  ) => {
    if (!company) return
    return (
      <Assessors>
        {assessors && assessors?.length > 0 ? (
          <StyledGroupedAvatars
            members={assessors
              .reduce((acc, as) => {
                company.allMembers.find((mem) => {
                  if (mem._id === as.memberId) {
                    acc.push({
                      name: mem.name,
                      avatar: mem.picture,
                    })
                  }
                })
                return acc
              }, [] as IMembers[])
              .filter(notEmpty)}
          />
        ) : !closed && !resultsViewTermOpen ? (
          <RoundButton
            round
            colorVariant="secondary"
            icon="Plus"
            onClick={(e) => {
              e?.stopPropagation()
              setSelectedEvaluationState(
                evaluationStates?.find(
                  (state) => state.evaluation.memberId === member._id
                )
              )
              setOpenAddModal({ member, type, assessors })
            }}
          />
        ) : null}
      </Assessors>
    )
  }

  const getTypeTranslation = (type: EvaluationAssessmentType) =>
    type === 'skills'
      ? t('general.skills').toLowerCase()
      : type === 'culture'
      ? t('general.feedback.culture').toLowerCase()
      : t('general.goals.goals').toLowerCase()

  const selectAssessors = (employee: ICompanyMember) => {
    if (
      openAddModal &&
      openAddModal.assessors &&
      openAddModal.assessors.some((as) => as.memberId === employee._id)
    ) {
      setOpenAddModal((state) => ({
        member: state!.member,
        type: state!.type,
        assessors:
          state!.assessors?.filter((as) => as.memberId !== employee._id) ||
          undefined,
      }))
    } else {
      setOpenAddModal((state) => ({
        member: state!.member,
        type: state!.type,
        assessors: [
          ...(state!.assessors || []),
          {
            memberId: employee._id,
            goals: openAddModal?.type === 'goals',
            skills: openAddModal?.type === 'skills',
            culture: openAddModal?.type === 'culture',
          },
        ],
      }))
    }
  }

  const checkChangedAssessors = (employee: ICompanyMember) => {
    const memberEvalState = evaluationStates?.find(
      (state) => state.evaluation.memberId === employee._id
    )
    const assessors = memberEvalState?.evaluation.assessors

    return (
      (openAddModal?.assessors?.length || 0) !==
        (assessors?.filter((as) => as[openAddModal!.type])?.length || 0) ||
      openAddModal?.assessors?.some(
        (as) =>
          !assessors?.find(
            (newAs) => newAs.memberId === as.memberId && as[openAddModal.type]
          )
      )
    )
  }

  const setSelectedMemberForModal = (member: ICompanyMember) => {
    setSelectedMember(member)
    const memberEvalState = evaluationStates?.find(
      (state) => state.evaluation.memberId === member._id
    )
    setSelectedEvaluationState(memberEvalState)
  }

  const renderVariableContacts = (
    filterMembers: ICompanyMember[],
    suggest: boolean
  ) => {
    if (!openAddModal || filterMembers.length === 0) return
    return (
      <VariableContacts>
        <Text
          textStyle="labelSmallCaps"
          color="text72"
          marginTop={12}
          marginBottom={12}
        >
          {t(
            suggest
              ? 'termEvaluationsProgress.suggestions'
              : 'termEvaluationsProgress.relevantPeople'
          )}
        </Text>
        {filterMembers.map((mem) => {
          const lead = isMemberStrictLeadOfMember(mem, openAddModal.member)
          const memberEvalState = evaluationStates?.find(
            (state) => state.evaluation.memberId === openAddModal.member._id
          )?.evaluation
          const alreadyRequested =
            planningComplete &&
            memberEvalState?.assessors?.some(
              (as) => as.memberId === mem._id && as[openAddModal.type]
            )

          return (
            <SelectContact
              key={`contact-type-${mem._id}`}
              contactMember={mem}
              title={mem.name}
              action={alreadyRequested ? undefined : () => selectAssessors(mem)}
              actionLabel={
                alreadyRequested
                  ? t('general.states.alreadyRequested')
                  : undefined
              }
              disabled={!!alreadyRequested}
              subtitle={
                suggest
                  ? t('termEvaluationsProgress.suggested')
                  : mem._id === openAddModal.member._id
                  ? undefined
                  : lead
                  ? t('termEvaluationsProgress.theirLead')
                  : t('addEditUser.buddy')
              }
              actionElement={
                !alreadyRequested && (
                  <StyledCheckbox
                    color="primary"
                    checked={
                      openAddModal.assessors &&
                      openAddModal.assessors.some(
                        (as) => as.memberId === mem._id
                      )
                    }
                  />
                )
              }
            />
          )
        })}
      </VariableContacts>
    )
  }

  const { buttonComponent } = contextButton([
    {
      links: [
        {
          label: t('termEvaluationsProgress.skipEvaluations'),
          icon: 'Next' as IconType,
          onClick: () => {
            appDispatch({
              type: 'updateAlertPopup',
              payload: {
                title: `${t('termEvaluationsProgress.skipEvaluations')}?`,
                subtitle: t(`general.areYouSure`),
                content: (
                  <Text textAlign="center">
                    {t(`termEvaluationsProgress.skipEvaluationsText`)}
                  </Text>
                ),
                actionButton: t('termEvaluationsProgress.skipEvaluations'),
                actionButtonDisabled: disableButton,
                dismissButton: t('general.actions.cancel'),
                onSubmit: closeEvaluations,
              },
            })
          },
        },
        phaseOpened &&
        !planningComplete &&
        !closed &&
        !selectedTerm?.suggestRequestsSent
          ? {
              label: t('termEvaluationsProgress.requestSuggestions'),
              icon: 'Buddy' as IconType,
              onClick: () => {
                appDispatch({
                  type: 'updateAlertPopup',
                  payload: {
                    title: t('termEvaluationsProgress.suggestTitle'),
                    content: (
                      <>
                        <MarkDownText textAlign="center">
                          {t(`termEvaluationsProgress.suggestText`)}
                        </MarkDownText>
                        <Text
                          textAlign="center"
                          textStyle="bodySmall"
                          marginTop={16}
                          color="text72"
                        >
                          {t(`termEvaluationsProgress.suggestCaption`)}
                        </Text>
                      </>
                    ),
                    actionButton: t('general.actions.sendRequests'),
                    actionButtonDisabled: disableButton,
                    dismissButton: t('general.actions.cancel'),
                    onSubmit: sendSuggestions,
                  },
                })
              },
            }
          : undefined,
      ].filter(notEmpty),
    },
  ])

  const selectedSuggestData = suggestData?.find(
    (s) => openAddModal?.member._id === s.memberId
  )

  const getInfoCardData = () => {
    return {
      title: t(
        resultsViewTermOpen
          ? `termEvaluationsProgress.titleResultsNotClosed`
          : overNotStarted
          ? 'termEvaluationsProgress.titleOverNotStarted'
          : evalsNotStarted
          ? 'termEvaluationsProgress.titleInfoNotStarted'
          : termOver
          ? 'termEvaluationsProgress.titleInfoClosed'
          : evalsNotOpenYet
          ? 'termEvaluationsProgress.titleInfoNotOpen'
          : 'termEvaluationsProgress.titleInfoActive'
      ),
      text: t(
        resultsViewTermOpen
          ? `termEvaluationsProgress.textResultsNotClosed`
          : overNotStarted
          ? 'termEvaluationsProgress.textOverNotStarted'
          : evalsNotStarted
          ? 'termEvaluationsProgress.textInfoNotStarted'
          : termOver
          ? 'termEvaluationsProgress.textInfoClosed'
          : evalsNotOpenYet
          ? 'termEvaluationsProgress.textInfoNotOpen'
          : 'termEvaluationsProgress.textInfoActive'
      ),
      icon:
        evalsNotStarted || evalsNotOpenYet
          ? 'Calendar'
          : overNotStarted || resultsViewTermOpen
          ? 'Warning'
          : 'List',
    }
  }

  const hidden = (self: boolean) =>
    (evaluationStates &&
      evaluationStates.length > 0 &&
      (evaluationStates[0].evaluation.hideAssessments[
        self ? 'self' : 'visitor'
      ] === undefined
        ? true
        : evaluationStates[0].evaluation.hideAssessments[
            self ? 'self' : 'visitor'
          ])) ||
    false

  const getRevealText = (config?: IRevealAssessmentsConfig, self?: boolean) => {
    const reveal = (option: EvaluationsRevealOptions) =>
      Object.values(config!).some((v) => v === option)
    if (config && reveal(EvaluationsRevealOptions.MANUAL)) {
      const isHidden = hidden(!!self)
      return {
        title: `${t('termEvaluationsProgress.revealTo')} ${
          self
            ? t('termEvaluationsProgress.subjects').toLowerCase()
            : t('termEvaluationsProgress.visitors').toLowerCase()
        }`,
        text: (
          <>
            {`${t('termEvaluationsProgress.resultsAre')} `}
            <Text color="primary" tag="span" textStyle="caption">
              {isHidden
                ? t('general.states.hidden')
                : t('general.states.shown')}
            </Text>
            {` ${t('termEvaluationsProgress.for')} ${
              self
                ? t('termEvaluationsProgress.subjectAssesment')
                : t('termEvaluationsProgress.subjectsAccess').toLowerCase()
            }`}
          </>
        ),
        switch: (
          <StyledSwitch
            isOn={!isHidden}
            icon={isHidden ? 'Hide' : 'Show'}
            alignEnd
            onToggle={() => {
              const actionText = `${
                isHidden
                  ? t('general.actions.reveal')
                  : t('general.actions.hide')
              } ${t('general.results').toLowerCase()}`
              appDispatch({
                type: 'updateAlertPopup',
                payload: {
                  title: t('termEvaluationsProgress.confirmTitle', {
                    verb: isHidden
                      ? t('termEvaluationsProgress.revealing')
                      : t('termEvaluationsProgress.hiding'),
                    people: self
                      ? t('termEvaluationsProgress.subjects').toLowerCase()
                      : t(
                          'termEvaluationsProgress.subjectsAccess'
                        ).toLowerCase(),
                  }),
                  subtitle: `${actionText}?`,
                  content: (
                    <>
                      <Text textAlign="center">
                        {t(
                          self
                            ? 'termEvaluationsProgress.confirmTextSelf'
                            : 'termEvaluationsProgress.confirmTextVisitor',
                          {
                            verb: isHidden
                              ? t('termEvaluationsProgress.can')
                              : t('termEvaluationsProgress.cant'),
                          }
                        )}
                      </Text>
                      <Text
                        textStyle="bodySmall"
                        color="textLight"
                        textAlign="center"
                        marginTop={24}
                      >
                        {t('termEvaluationsProgress.adminsCanChange', {
                          verb: (isHidden
                            ? t('general.actions.hide')
                            : t('general.actions.reveal')
                          ).toLowerCase(),
                        })}
                      </Text>
                    </>
                  ),
                  actionButton: actionText,
                  dismissButton: t('general.actions.cancel'),
                  onSubmit: async () => {
                    if (!selectedTerm) return
                    await editHideAssessmentsService(
                      String(selectedTerm.termNumber),
                      self ? !isHidden : hidden(true),
                      !self ? !isHidden : hidden(false),
                      userAuthToken || ''
                    )
                    appDispatch({
                      type: 'updateAlertPopup',
                      payload: undefined,
                    })
                    await loadStates()
                  },
                },
              })
            }}
          />
        ),
      }
    } else if (
      !config ||
      reveal(EvaluationsRevealOptions.AFTER_SELF_ASSESSING)
    ) {
      return {
        text: t(
          self
            ? 'termEvaluationsProgress.revealSubjectSelfAssessment'
            : 'termEvaluationsProgress.revealVisitorSelfAssessment'
        ),
      }
    } else if (config && reveal(EvaluationsRevealOptions.AFTER_COMPLETION)) {
      return {
        text: t(
          self
            ? 'termEvaluationsProgress.revealSubjectCompletion'
            : 'termEvaluationsProgress.revealVisitorCompletion'
        ),
      }
    } else if (config && reveal(EvaluationsRevealOptions.IMMEDIATELY)) {
      return {
        text: t(
          self
            ? 'termEvaluationsProgress.revealSubjectImmediately'
            : 'termEvaluationsProgress.revealVisitorImmediately'
        ),
      }
    }
  }

  const filterPanelEl = (
    <StickyFilterPanel
      filters={visibleFilters}
      prependCustom={termFilterEl}
      extraFilters={extraFilters}
      extraChips={extraChips}
      extraHasActive={extraHasActive}
      onExtraClear={clearExtraFilters}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      countLabel={countLabel}
      actions={
        activeTab === 'progress' ? (
          <RowAligned>
            <ToolbarButton
              alignEnd
              colorVariant="secondary"
              iconSize={20}
              icon="Statistics"
              overrideOnHover={false}
              disabled={!statistics}
              hoverCaption={
                <Icon
                  type={openStatistics ? 'ChevronUp' : 'ChevronDown'}
                  size={12}
                  color={
                    !statistics ? theme.colors.textLight : theme.colors.primary
                  }
                />
              }
              caption={
                <Icon
                  type={openStatistics ? 'ChevronDown' : 'ChevronUp'}
                  size={12}
                  color={theme.colors.primary}
                />
              }
              onClick={() => {
                setOpenStatistics(!openStatistics)
              }}
            />
          </RowAligned>
        ) : undefined
      }
    />
  )

  return (
    <AppLayout
      navbarHidden={openConfirmation}
      secondaryNav={{
        title: teamFromNav
          ? `${t('general.team')} ${t(
              'general.evaluation_other'
            ).toLowerCase()}`
          : openConfirmation
          ? t('termEvaluationsProgress.start')
          : t('general.evaluation_other'),
        subtitle:
          !openConfirmation && !teamFromNav
            ? t('termEvaluationsProgress.subtitle')
            : undefined,
        version: openConfirmation ? 'form' : 'subPage',
        tabs: isAdmin
          ? openConfirmation
            ? undefined
            : [
                {
                  name: 'progress',
                  title: t('termEvaluationsProgress.planningProgress'),
                  active: activeTab === 'progress',
                  disabled: !openEvals || openEvals.length === 0,
                  path: '/company/rituals/evaluations/progress',
                  onClick: () => {
                    setActiveTab('progress')
                    setSelectedTerm(
                      openEvals?.length
                        ? openEvals[openEvals.length - 1]
                        : undefined
                    )
                    scrollToPinnedTabs()
                  },
                },
                resultEvals && resultEvals.length > 0
                  ? {
                      name: 'history',
                      title: t('general.results'),
                      active: activeTab === 'results',
                      path: '/company/rituals/evaluations/results',
                      onClick: () => {
                        setActiveTab('results')
                        if (resultEvals && resultEvals.length > 0) {
                          setSelectedTerm(resultEvals[resultEvals.length - 1])
                        }
                        scrollToPinnedTabs()
                      },
                    }
                  : undefined,
              ].filter(notEmpty)
          : undefined,
        breadcrumb: openConfirmation
          ? undefined
          : breadCrumb && breadCrumb.length > 0
          ? [
              {
                name: t('general.actions.backTo', {
                  page: t(breadCrumb[breadCrumb.length - 1].name).toLowerCase(),
                }),
                route: breadCrumb[breadCrumb.length - 1].route,
              },
            ]
          : undefined,
        button: !openConfirmation
          ? undefined
          : {
              onClick: () => {
                setOpenConfirmation(false)
                setTermName(selectedTerm?.name || '')
              },
              icon: 'Close',
              caption: t('general.actions.back'),
              colorVariant: 'secondary',
            },
      }}
    >
      <Helmet title={t('performanceEvent.termEvaluations')} />
      {selectedMember && selectedEvaluationState && (
        <EvaluationModal
          pdp={selectedEvaluationState.pdp!}
          closeModal={() => {
            setSelectedEvaluationState(undefined)
            setSelectedMember(undefined)
            setOpenAddModal(undefined)
          }}
          isOwnProfile={selectedMember === user?.asCompanyMember}
          addAssessor={setOpenAddModal}
          initialType={
            !closed && !resultsViewTermOpen ? 'addAssessors' : 'viewAssessors'
          }
          evalCompany={termState?.companyVersions?.find(
            (c) => c.__v === selectedEvaluationState.version
          )}
          evaluations={modalEvaluations}
          member={
            (termState?.companyVersions
              ?.find((c) => c.__v === selectedEvaluationState.version)
              ?.members.find((m) => m._id === selectedMember?._id) ||
              selectedMember ||
              user!.asCompanyMember!) as ICompanyMember
          }
          removeAssessor={removeAssessors}
          reopenAssessment={reopenAssessment}
          previousEvaluation={
            (compareTo
              ? prevEvaluationStates
              : autoPrevTermState?.memberStates
            )?.find((s) => s.evaluation.memberId === selectedMember?._id)
              ?.evaluation
          }
          compareCompanyVersion={(compareTo
            ? prevTermState
            : autoPrevTermState
          )?.companyVersions?.find(
            (c) =>
              c.__v ===
              (compareTo
                ? prevEvaluationStates
                : autoPrevTermState?.memberStates
              )?.find((s) => s.evaluation.memberId === selectedMember?._id)
                ?.version
          )}
        />
      )}
      {openEditModal && (
        <DialogScreen
          actionButton={t('general.actions.save')}
          onSubmit={renameEvaluation}
          content={{
            title: t(`termEvaluationsProgress.renameTitle`),
            onDismiss: () => {
              setTermName(selectedTerm?.name || '')
              setOpenEditModal(false)
            },
            actionButtonDisabled:
              termName.length < 2 ||
              selectedTerm?.name === termName ||
              disableButton,
            content: (
              <>
                <Text textAlign="center" marginBottom={12}>
                  {t('termEvaluationsProgress.renameSubtitle')}
                </Text>
                <Input
                  value={termName}
                  onChange={(event) => {
                    setTermName(event.currentTarget.value)
                  }}
                />
              </>
            ),
          }}
        />
      )}
      {openConfirmation ? (
        <StartConfirmation
          name={termName}
          setName={setTermName}
          finalize={finalizePlanning}
          disableButton={disableButton}
        />
      ) : (
        <GridRow alignType="center">
          <GridCol xs={12}>
            {isLoading ? (
              <Loader />
            ) : (
              <>
                {filterPanelEl}
                <Sections>
                  <HeaderRow>
                    <RowAligned>
                      <Text textStyle="displayS" marginRight={24}>
                        {selectedTerm?.name}
                      </Text>
                      {!planningComplete && (
                        <Button
                          icon="Pencil"
                          colorVariant="secondary"
                          onClick={() => setOpenEditModal(true)}
                        />
                      )}
                      {/* Export this round's results, at the end of its title row */}
                      {activeTab === 'results' && canExportResults && (
                        <RowAligned margin="0 0 0 auto">
                          <Button
                            icon="Download"
                            colorVariant="secondary"
                            disabled={isExporting}
                            caption={
                              isExporting
                                ? t('fileSettings.generating')
                                : t('collectionExport.exportButton')
                            }
                            onClick={exportResults}
                          />
                        </RowAligned>
                      )}
                      {activeTab !== 'results' && !closed && (
                        <RowAligned margin="0 0 0 auto">
                          {evalsNotStarted && (
                            <StyledButton>{buttonComponent()}</StyledButton>
                          )}
                          <div>
                            <CenterButton
                              colorVariant={'primary'}
                              disabled={
                                evalsNotOpenYet ||
                                disableButton ||
                                (!termOver && planningComplete)
                              }
                              caption={t(
                                evalsNotStarted || evalsNotOpenYet
                                  ? 'termEvaluationsProgress.start'
                                  : 'termEvaluationsProgress.close'
                              )}
                              onClick={() => {
                                if (evalsNotStarted) {
                                  setOpenConfirmation(true)
                                } else {
                                  appDispatch({
                                    type: 'updateAlertPopup',
                                    payload: {
                                      title: t(
                                        `termEvaluationsProgress.closeModal.title`
                                      ),
                                      subtitle: t(
                                        `termEvaluationsProgress.closeModal.subtitle`
                                      ),
                                      content: (
                                        <Text textAlign="center" tag="div">
                                          {t(
                                            `termEvaluationsProgress.closeModal.intro`
                                          )}
                                          <br />
                                          <Text
                                            textStyle="bodySmall"
                                            textAlign="center"
                                            marginTop={12}
                                            tag="div"
                                          >
                                            {t(
                                              `termEvaluationsProgress.closeModal.actionDescription`
                                            )}
                                            <ul style={{ marginTop: 0 }}>
                                              <li>
                                                {t(
                                                  `termEvaluationsProgress.closeModal.listItemOne`
                                                )}
                                              </li>
                                              <li>
                                                {t(
                                                  `termEvaluationsProgress.closeModal.listItemTwo`
                                                )}
                                              </li>
                                            </ul>
                                          </Text>
                                        </Text>
                                      ),
                                      actionButton: t(
                                        'termEvaluationsProgress.close'
                                      ),
                                      actionButtonDisabled: disableButton,
                                      dismissButton: t(
                                        'general.actions.cancel'
                                      ),
                                      onSubmit: closeEvaluations,
                                    },
                                  })
                                }
                              }}
                            />
                            {(evalsNotOpenYet || evalsActive) &&
                              !resultsViewTermOpen && (
                                <Text
                                  marginTop={4}
                                  color="primary"
                                  textStyle="caption"
                                  textAlign="center"
                                >{`${t(
                                  'termEvaluationsProgress.availableOn'
                                )} ${
                                  evalsNotOpenYet && termDate
                                    ? formatDate(termDate)
                                    : termEndDate && formatDate(termEndDate)
                                }`}</Text>
                              )}
                          </div>
                        </RowAligned>
                      )}
                    </RowAligned>
                    {startTermDate && termEndDate && (
                      <DateStrip margin="8px 0 0">
                        <DateSegment>
                          <Icon
                            size={16}
                            type="Calendar"
                            color={theme.colors.primary}
                          />
                          <Text textStyle="bodySmall" color="primary">
                            {formatDate(startTermDate)}
                          </Text>
                          <Text textStyle="bodySmall" color="textLight">
                            {t('termEvaluationsProgress.to')}
                          </Text>
                          <Text textStyle="bodySmall" color="primary">
                            {formatDate(termEndDate)}
                          </Text>
                        </DateSegment>
                        {planningDate && (
                          <DateSegment>
                            <Icon
                              size={16}
                              type="ArrowRight"
                              color={theme.colors.primary}
                            />
                            <Text textStyle="bodySmall" color="textLight">
                              {t('termEvaluationsProgress.startedOn')}
                            </Text>
                            <Text textStyle="bodySmall" color="primary">
                              {formatDate(planningDate)}
                            </Text>
                          </DateSegment>
                        )}
                        {closeDate && (
                          <DateSegment>
                            <Icon
                              size={16}
                              type="Check"
                              color={theme.colors.primary}
                            />
                            <Text textStyle="bodySmall" color="textLight">
                              {t('termEvaluationsProgress.closedOn')}
                            </Text>
                            <Text textStyle="bodySmall" color="primary">
                              {formatDate(closeDate)}
                            </Text>
                          </DateSegment>
                        )}
                      </DateStrip>
                    )}
                  </HeaderRow>
                  {!closed && isAdmin && (
                    <InfoCards>
                      <InfoCard
                        warn={overNotStarted || resultsViewTermOpen}
                        compact
                      >
                        <Icon
                          type={getInfoCardData().icon as IconType}
                          size={24}
                          color={
                            overNotStarted || resultsViewTermOpen
                              ? theme.colors.secondary
                              : theme.colors.textLight
                          }
                        />
                        <div>
                          <Text
                            color={
                              overNotStarted || resultsViewTermOpen
                                ? 'secondary'
                                : 'text'
                            }
                            textStyle="labelHeavy"
                          >
                            {getInfoCardData().title}
                          </Text>
                          <Text textStyle="bodySmall" marginTop={4}>
                            {getInfoCardData().text}
                          </Text>
                        </div>
                      </InfoCard>
                      {((evalsNotStarted &&
                        !selectedTerm?.suggestRequestsSent) ||
                        planningComplete) &&
                        activeTab !== 'results' && (
                          <InfoCard resultsCard={planningComplete}>
                            {evalsNotStarted &&
                            !selectedTerm?.suggestRequestsSent ? (
                              <>
                                <Icon
                                  type="Buddy"
                                  size={24}
                                  color={theme.colors.textLight}
                                />
                                <Text textStyle="labelHeavy" marginTop={16}>
                                  {t('termEvaluationsProgress.titleSuggest')}
                                </Text>
                                <Text textStyle="bodySmall" marginTop={4}>
                                  {t('termEvaluationsProgress.textSuggest')}
                                </Text>
                              </>
                            ) : (
                              <>
                                <RowAlignedBorder>
                                  <StyledIcon
                                    type="User"
                                    size={16}
                                    color={theme.colors.primary}
                                  />
                                  <div>
                                    {getRevealText(revealResultsSelf, true)
                                      ?.title && (
                                      <Text
                                        textStyle="captionHeavy"
                                        marginBottom={4}
                                      >
                                        {
                                          getRevealText(revealResultsSelf, true)
                                            ?.title
                                        }
                                      </Text>
                                    )}
                                    <Text textStyle="caption">
                                      {
                                        getRevealText(revealResultsSelf, true)
                                          ?.text
                                      }
                                    </Text>
                                  </div>
                                  {
                                    getRevealText(revealResultsSelf, true)
                                      ?.switch
                                  }
                                </RowAlignedBorder>
                                <RowAligned margin="12px 0 0">
                                  <StyledIcon
                                    type="Buddy"
                                    size={16}
                                    color={theme.colors.primary}
                                  />
                                  <div>
                                    {getRevealText(revealResultsVisitors)
                                      ?.title && (
                                      <Text
                                        textStyle="captionHeavy"
                                        marginBottom={4}
                                      >
                                        {
                                          getRevealText(revealResultsVisitors)
                                            ?.title
                                        }
                                      </Text>
                                    )}
                                    <Text textStyle="caption">
                                      {
                                        getRevealText(revealResultsVisitors)
                                          ?.text
                                      }
                                    </Text>
                                  </div>
                                  {getRevealText(revealResultsVisitors)?.switch}
                                </RowAligned>
                              </>
                            )}
                          </InfoCard>
                        )}
                    </InfoCards>
                  )}

                  {openStatistics && statistics && activeTab === 'progress' && (
                    <StatsGridRow alignType="center">
                      <GridCol xs={12} m={6}>
                        <BorderCard>
                          <CircularProgress
                            firstValueName={t(
                              'termEvaluationsProgress.incomplete'
                            )}
                            secondValueName={t(
                              'termEvaluationsProgress.complete'
                            )}
                            value={statistics.doneEvaluations}
                            maxValue={statistics.totalEvaluations}
                            text={t(
                              'termEvaluationsProgress.completedEvaluations'
                            )}
                          />
                        </BorderCard>
                      </GridCol>
                      <GridCol xs={12} m={6}>
                        <BorderCard>
                          <AssessmentsProgress
                            average={statistics.averageSkillAssessments}
                            variant="skills"
                            percentage={statistics.percentageSkillAssessments}
                            total={statistics.totalSkillAssessments}
                          />
                          <AssessmentsProgress
                            average={statistics.averageCultureAssessments}
                            variant="culture"
                            percentage={statistics.percentageCultureAssessments}
                            total={statistics.totalCultureAssessments}
                          />
                          <AssessmentsProgress
                            average={statistics.averageGoalAssessments}
                            variant="goals"
                            percentage={statistics.percentageGoalAssessments}
                            total={statistics.totalGoalAssessments}
                          />
                        </BorderCard>
                      </GridCol>
                    </StatsGridRow>
                  )}

                  {activeTab === 'results' && selectedTerm && (
                    <ResultsAnalyticsBlock>
                      <KPIStrip
                        current={resultAverages}
                        previous={compareTo ? prevResultAverages : undefined}
                        compareActive={!!compareTo}
                      />
                      <CultureStrip
                        selectedTerm={selectedTerm}
                        compareTo={compareTo}
                        memberIds={visibleMembers.map((member) => member._id)}
                      />
                      <DistributionBlock>
                        <DistributionHeader>
                          <Text textStyle="title">
                            {t('insights.companyGrid')}
                          </Text>
                          {nineGridToggle}
                        </DistributionHeader>
                        <Insights
                          selectedTerm={selectedTerm}
                          compareTo={compareTo}
                          activeMemberIds={activeMemberIds}
                          view={nineGridView}
                          setMemberForModal={setSelectedMemberForModal}
                        />
                      </DistributionBlock>
                    </ResultsAnalyticsBlock>
                  )}

                  {activeTab === 'results' && selectedTerm && !compareTo && (
                    <TableToolbar>
                      <Text textStyle="title">
                        {t('insights.detailedResults')}
                      </Text>
                      <SelfResultsToggle
                        type="button"
                        onClick={() => setShowSelfResults(!showSelfResults)}
                        aria-pressed={showSelfResults}
                      >
                        <Icon
                          type={showSelfResults ? 'Show' : 'Hide'}
                          size={16}
                          color={
                            showSelfResults
                              ? theme.colors.primary
                              : theme.colors.textLight
                          }
                        />
                        <Text
                          textStyle="label"
                          color={showSelfResults ? 'primary' : 'textLight'}
                          tag="span"
                        >
                          {t(
                            showSelfResults
                              ? 'termEvaluationsProgress.hideSelfResults'
                              : 'termEvaluationsProgress.showSelfResults'
                          )}
                        </Text>
                      </SelfResultsToggle>
                    </TableToolbar>
                  )}

                  {company && (
                    <StyledTable
                      key={activeTab}
                      emptyState={getFilterEmptyState(t, {
                        filters: visibleFilters,
                        searchTerm,
                        extraHasActive,
                        onClear: clearAllFiltersAndSearch,
                      })}
                      // Progress names the three assessor columns in a
                      // light caption row; results name them in the header
                      headerCaptions={
                        activeTab === 'progress'
                          ? [
                              '',
                              ...(['goals', 'skills', 'culture'] as const).map(
                                (type) => (
                                  <Text
                                    textStyle="labelTinyCaps"
                                    color="textLight"
                                  >
                                    {getTypeTranslation(type)}
                                  </Text>
                                )
                              ),
                              planningComplete ? '' : undefined,
                              '',
                            ].filter(notEmpty)
                          : undefined
                      }
                      mainHeaders={
                        activeTab === 'results'
                          ? [
                              { label: t('general.users.name'), sort: 'text' },
                              ...(['goals', 'skills', 'culture'] as const).map(
                                (type) => ({
                                  label: `${getTypeTranslation(type)} ${t(
                                    'evaluationModal.score'
                                  ).toLowerCase()}`,
                                  sort: 'number' as const,
                                })
                              ),
                              { label: t('general.details'), align: 'end' },
                            ]
                          : [
                              t('general.users.name'),
                              t('termEvaluationsProgress.assessors'),
                              '',
                              '',
                              planningComplete
                                ? t('general.states.progress')
                                : undefined,
                              {
                                label: t('general.details'),
                                align: 'end' as const,
                              },
                            ].filter(notEmpty)
                      }
                      defaultSort={
                        activeTab === 'results'
                          ? { column: 0, direction: 'asc' }
                          : undefined
                      }
                      columnWidths={
                        planningComplete && activeTab === 'progress'
                          ? ['30%', '15%', '15%', '15%', '15%', '10%']
                          : activeTab === 'results'
                          ? ['30%', '18%', '21%', '21%', '10%']
                          : ['45%', '15%', '15%', '15%', '10%']
                      }
                      mainData={[
                        ...visibleMembers.map((member) => {
                          const memberEvalState = evaluationStates?.find(
                            (state) => state.evaluation.memberId === member._id
                          )

                          // Use the company version attached to the evaluation to display correct results
                          const companyVersionOfEvaluation = termState?.companyVersions?.find(
                            (c) => c.__v === memberEvalState?.version
                          )
                          // Transform from ICompanyPure to ICompany
                          const companyOfEvaluation = companyVersionOfEvaluation
                            ? adaptBackendCompanyToFrontendCompany(
                                companyVersionOfEvaluation
                              )
                            : company
                          // fall back to current settings if no settings were present yet in company version
                          if (
                            !companyOfEvaluation.settings ||
                            !companyOfEvaluation.settings.evaluations
                          ) {
                            companyOfEvaluation.settings = company.settings
                          }

                          const memberInCompanyVersion =
                            companyOfEvaluation.members.find(
                              (m) => m._id === member._id
                            ) || member

                          const withoutSkills = memberHasNoSkills(
                            memberInCompanyVersion,
                            companyOfEvaluation
                          )

                          const getEvaluationWithFilteredContributors = (
                            evaluation: IEvaluationPure | IEvaluationState
                          ): IEvaluationPure | IEvaluationState => ({
                            ...evaluation,
                            assessment: evaluation.assessment.filter((as) =>
                              getAllowedContributors(
                                companyOfEvaluation,
                                memberInCompanyVersion
                              ).some((s) => s._id === as.senderId)
                            ),
                          })

                          const hasGoals =
                            memberEvalState?.pdp &&
                            memberEvalState.pdp.goals.length > 0 &&
                            [
                              PdpStatus.PUBLISHED,
                              PdpStatus.IN_EVALUATION,
                            ].includes(memberEvalState.pdp.status)

                          const goalAssessors = memberEvalState?.evaluation.assessors.filter(
                            (as) => as.goals
                          )
                          const skillAssessors = memberEvalState?.evaluation.assessors.filter(
                            (as) => as.skills
                          )
                          const cultureAssessors = memberEvalState?.evaluation.assessors.filter(
                            (as) => as.culture
                          )

                          const assessments =
                            memberEvalState?.evaluation.assessment

                          const selfAssessment = assessments?.find(
                            (as) => as.senderId === member._id
                          )

                          const hasAssessors =
                            memberEvalState?.evaluation.assessors &&
                            memberEvalState?.evaluation.assessors.length > 0

                          const notStarted =
                            memberEvalState?.evaluation.status ===
                            EvaluationStatus.NOT_STARTED
                          const inProgress =
                            memberEvalState?.evaluation.status ===
                            EvaluationStatus.IN_PROGRESS

                          const progress = assessments
                            ? (assessments.filter(
                                (as) =>
                                  as.status === SingleEvaluationStatus.DONE
                              ).length /
                                assessments.filter(
                                  (as) =>
                                    as.status !==
                                    SingleEvaluationStatus.NOT_COMPLETE_IN_TIME
                                ).length) *
                              100
                            : 0

                          const goalAvg =
                            memberEvalState &&
                            getAvgGoalEvaluations(
                              getEvaluationWithFilteredContributors(
                                memberEvalState.evaluation
                              )
                            )

                          const achievedGoalsNum = goalAvg?.filter(
                            (goal) => goal.goalStatus === 3 // achieved value
                          ).length

                          const filteredAssessments = assessments?.filter((a) =>
                            getAllowedContributors(
                              companyOfEvaluation,
                              memberInCompanyVersion
                            ).some((s) => s._id === a.senderId)
                          )

                          const skillsMap =
                            filteredAssessments &&
                            calculateAverageExpectationValues(
                              filteredAssessments.filter(
                                (a) => a.skillsSubmitted
                              )
                            )

                          let total = 0
                          skillsMap?.forEach((avg) => {
                            total += avg
                          })
                          const skillAvg =
                            !skillsMap || skillsMap.size === 0
                              ? undefined
                              : total / skillsMap.size
                          const skillValues = company && getSkillScores(company)

                          const culturalValues =
                            company && getCultureScores(company)
                          const cultureMap =
                            filteredAssessments &&
                            calculatePercentageAlignmentValue(
                              filteredAssessments.filter(
                                (a) => a.cultureSubmitted
                              )
                            )

                          let cTotal = 0
                          cultureMap?.forEach((avg) => {
                            cTotal += avg
                          })
                          const cultureAvg =
                            !cultureMap || cultureMap.size === 0
                              ? undefined
                              : cTotal / cultureMap.size

                          // Prev averages for delta cells when compareTo active
                          const prevMemberEvalState = prevEvaluationStates?.find(
                            (state) => state.evaluation.memberId === member._id
                          )
                          const prevCompanyVersion = prevTermState?.companyVersions?.find(
                            (c) => c.__v === prevMemberEvalState?.version
                          )
                          const prevCompany = prevCompanyVersion
                            ? adaptBackendCompanyToFrontendCompany(
                                prevCompanyVersion
                              )
                            : company
                          // fall back to current settings if no settings were present yet in company version
                          if (
                            !prevCompany.settings ||
                            !prevCompany.settings.evaluations
                          ) {
                            prevCompany.settings = company.settings
                          }
                          const prevMemberInCompany =
                            prevCompany?.members.find(
                              (m) => m._id === member._id
                            ) || member

                          const prevFilteredAssessments = prevMemberEvalState?.evaluation.assessment?.filter(
                            (a) =>
                              prevCompany
                                ? getAllowedContributors(
                                    prevCompany,
                                    prevMemberInCompany
                                  ).some((s) => s._id === a.senderId)
                                : false
                          )

                          const prevSkillsMap =
                            prevFilteredAssessments &&
                            calculateAverageExpectationValues(
                              prevFilteredAssessments.filter(
                                (a) => a.skillsSubmitted
                              )
                            )
                          let prevSkillTotal = 0
                          prevSkillsMap?.forEach((avg) => {
                            prevSkillTotal += avg
                          })
                          const prevSkillAvg =
                            !prevSkillsMap || prevSkillsMap.size === 0
                              ? undefined
                              : prevSkillTotal / prevSkillsMap.size

                          const prevCultureMap =
                            prevFilteredAssessments &&
                            calculatePercentageAlignmentValue(
                              prevFilteredAssessments.filter(
                                (a) => a.cultureSubmitted
                              )
                            )
                          let prevCultureTotal = 0
                          prevCultureMap?.forEach((avg) => {
                            prevCultureTotal += avg
                          })
                          const prevCultureAvg =
                            !prevCultureMap || prevCultureMap.size === 0
                              ? undefined
                              : prevCultureTotal / prevCultureMap.size

                          // Goals score is the share of goals achieved
                          const goalScore =
                            !compareTo &&
                            hasGoals &&
                            goalAssessors?.length &&
                            goalAvg?.length
                              ? (achievedGoalsNum || 0) / goalAvg.length
                              : undefined

                          return {
                            id: member._id,
                            sortValues: [
                              member.name,
                              goalScore,
                              skillAvg,
                              cultureAvg,
                            ],
                            onClick: !hasAssessors
                              ? undefined
                              : () => {
                                  setSelectedMember(member)
                                  setSelectedEvaluationState(memberEvalState)
                                },
                            elements: [
                              <UserDetails
                                key={member._id}
                                user={memberInCompanyVersion || member}
                                version="job"
                              />,
                              activeTab === 'results' ? (
                                compareTo ? (
                                  ''
                                ) : hasGoals && goalAssessors?.length ? (
                                  goalAvg && goalAvg.length > 0 ? (
                                    <StyledGoalCount
                                      darkerTags
                                      icon="Goals"
                                      actions={[
                                        {
                                          total: goalAvg?.length || 0,
                                          complete: achievedGoalsNum || 0,
                                        },
                                      ]}
                                    />
                                  ) : (
                                    noEvaluationTag(member, 'goals', true)
                                  )
                                ) : (
                                  ''
                                )
                              ) : hasGoals ? (
                                assessorElements(member, 'goals', goalAssessors)
                              ) : (
                                noEvaluationTag(member, 'goals')
                              ),
                              withoutSkills && skillAssessors?.length === 0 ? (
                                noEvaluationTag(member, 'skills')
                              ) : activeTab === 'results' && compareTo ? (
                                skillAvg !== undefined ? (
                                  <DeltaPill
                                    current={skillAvg}
                                    previous={prevSkillAvg}
                                  />
                                ) : (
                                  noEvaluationTag(member, 'skills', true)
                                )
                              ) : activeTab === 'results' ? (
                                skillAssessors?.length ? (
                                  skillAvg !== undefined && skillValues ? (
                                    resultsView(skillValues, skillAvg, {
                                      as: selfAssessment,
                                      member,
                                      type: 'skills',
                                    })
                                  ) : (
                                    noEvaluationTag(member, 'skills', true)
                                  )
                                ) : (
                                  ''
                                )
                              ) : (
                                assessorElements(
                                  member,
                                  'skills',
                                  skillAssessors
                                )
                              ),
                              activeTab === 'results' && compareTo ? (
                                cultureAvg !== undefined ? (
                                  <DeltaPill
                                    current={cultureAvg}
                                    previous={prevCultureAvg}
                                  />
                                ) : (
                                  noEvaluationTag(member, 'culture', true)
                                )
                              ) : activeTab === 'results' ? (
                                cultureAssessors?.length ? (
                                  cultureAvg !== undefined && culturalValues ? (
                                    resultsView(culturalValues, cultureAvg, {
                                      as: selfAssessment,
                                      member,
                                      type: 'culture',
                                    })
                                  ) : (
                                    noEvaluationTag(member, 'culture', true)
                                  )
                                ) : (
                                  ''
                                )
                              ) : (
                                assessorElements(
                                  member,
                                  'culture',
                                  cultureAssessors
                                )
                              ),
                              planningComplete &&
                              hasAssessors &&
                              activeTab === 'progress' ? (
                                progress === 100 ? (
                                  <Icon
                                    size={12}
                                    type="Check"
                                    color={theme.colors.primary}
                                  />
                                ) : (
                                  <StyledProgressBar
                                    percentage={progress}
                                    color={theme.colors.primary}
                                    backgroundColor={
                                      theme.colors.primaryFadeLight
                                    }
                                  />
                                )
                              ) : planningComplete &&
                                activeTab === 'progress' ? (
                                ''
                              ) : undefined,
                              hasAssessors ? (
                                <DetailRow>
                                  {activeTab === 'results' &&
                                    (notStarted || inProgress) && (
                                      <StyledStatusTag
                                        content={
                                          <StyledTag
                                            caption={
                                              <>
                                                {inProgress && (
                                                  <Icon
                                                    type="Warning"
                                                    color={
                                                      theme.colors.textLight
                                                    }
                                                    size={16}
                                                  />
                                                )}
                                                <Text textStyle="caption">
                                                  {t(
                                                    notStarted
                                                      ? 'general.states.notStarted'
                                                      : 'termEvaluationsProgress.incomplete'
                                                  )}
                                                </Text>
                                              </>
                                            }
                                            color="textLightest"
                                          />
                                        }
                                      />
                                    )}
                                  <Icon
                                    size={16}
                                    type="ChevronRight"
                                    color={theme.colors.primaryFadeLight}
                                  />
                                </DetailRow>
                              ) : (
                                ''
                              ),
                            ].filter(notEmpty),
                          }
                        }),
                      ]}
                    />
                  )}
                </Sections>
              </>
            )}
          </GridCol>
        </GridRow>
      )}
      {openAddModal && (
        <ContentModal
          title={t('termEvaluationsProgress.addModalTitle', {
            name: openAddModal.member.firstName,
            type: getTypeTranslation(openAddModal.type),
          })}
          subtitle={t('termEvaluationsProgress.addModalSubtitle', {
            name: openAddModal.member.firstName,
            type: getTypeTranslation(openAddModal.type),
          })}
          peoplePickerVersion={{
            members: company?.members || [],
            requestPendingIds:
              planningComplete &&
              evaluationStates?.find(
                (state) => state.evaluation.memberId === openAddModal.member._id
              )?.evaluation.assessors
                ? evaluationStates
                    ?.find(
                      (state) =>
                        state.evaluation.memberId === openAddModal.member._id
                    )
                    ?.evaluation.assessors.filter((as) =>
                      openAddModal.type === 'goals'
                        ? as.goals
                        : openAddModal.type === 'culture'
                        ? as.culture
                        : as.skills
                    )
                    .map((as) => as.memberId)
                : undefined,
            selectedMemberIds: openAddModal.assessors
              ? openAddModal.assessors.map((as) => as.memberId)
              : undefined,
            multipleSelect: true,
            action: (employee) => {
              selectAssessors(employee)
            },
          }}
          bottomComponents={({ isFiltering }) => (
            <>
              {!isFiltering &&
                renderVariableContacts(
                  company!.members.filter(
                    (mem) =>
                      mem._id === openAddModal.member._id ||
                      isMemberBuddyOfAnotherMember(mem, openAddModal.member) ||
                      isMemberStrictLeadOfMember(mem, openAddModal.member)
                  ),
                  false
                )}
              {selectedSuggestData &&
                renderVariableContacts(
                  selectedSuggestData.closeMembersIds
                    .map((id) => company?.members.find((m) => m._id === id))
                    .filter(notEmpty),
                  true
                )}
              {checkChangedAssessors(openAddModal.member) && (
                <StyledFormFinish
                  text={
                    planningComplete
                      ? t('termEvaluationsProgress.sendRequestNow', {
                          type: getTypeTranslation(openAddModal.type),
                          name: openAddModal.member.firstName,
                        })
                      : t('termEvaluationsProgress.changesMade')
                  }
                  buttonText={t(
                    planningComplete
                      ? 'general.actions.sendNow'
                      : 'general.actions.save'
                  )}
                  onClick={async () => {
                    await addAssessors(openAddModal)
                  }}
                  disableButton={disableButton}
                />
              )}
            </>
          )}
          closeModal={() => {
            setOpenAddModal(undefined)
          }}
        />
      )}
    </AppLayout>
  )
}

export default TermEvaluationsProgress

const StyledSwitch = styled(Switch)<{ alignEnd?: boolean }>`
  ${({ alignEnd }) => alignEnd && 'margin-left: auto;'}
`

const StyledStatusTag = styled(JobTag)`
  padding: 0;
  border-radius: 50px;
  background: ${({ theme }) => theme.colors.primaryLight};
  margin-right: 8px;
`

const DetailRow = styled(RowAligned)`
  justify-content: flex-end;
`

const StyledTextResult = styled.span`
  display: none;
`

const CenterButton = styled(Button)`
  margin: 0 auto;
`

const ResultTag = styled(StatusTag)`
  text-transform: initial;
  display: flex;
  align-items: center;
  width: calc(100% - 16px);
`

const StyledValueWrapper = styled(ValueWrapper)`
  gap: 2px;
  border-radius: 24px;
  overflow: hidden;
  margin: 4px 0;
  height: 8px;
  width: 100%;
`

const StyledGoalCount = styled(GoalCount)`
  span {
    font-weight: ${FontWeight.Regular};
  }

  svg > path {
    fill: ${({ theme }) => theme.colors.textLight};
  }

  ${CheckIcon} {
    display: none;
  }
`

const StyledFormFinish = styled(FormFinish)`
  ${mqFrom.M`
     bottom: 90px;
  `}
`

const StyledCheckbox = styled(Checkbox)`
  margin-left: auto;
`

const VariableContacts = styled.div`
  margin-bottom: 24px;
`

const StyledProgressBar = styled(ProgressBar)`
  height: 8px;
  transition: background-color ${fastTransition};

  > div {
    height: 8px;
  }
`

const StyledTable = styled(Table)`
  ${MainRow} {
    &:hover {
      ${ResultTag} {
        display: inline-flex;
        width: auto;
      }
      ${StyledTextResult} {
        display: flex;
      }
      ${StyledValueWrapper} {
        display: none;
      }
      ${StyledProgressBar} {
        background-color: ${({ theme }) => theme.colors.lightest};
      }
    }
  }
`

const Assessors = styled(RowAligned)`
  display: inline-flex;
`

export const RoundButton = styled(Button)`
  width: 32px;
  height: 32px;
`

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
`

// Date strip wraps segment-by-segment on narrow screens instead of breaking
// individual dates mid-value.
const DateStrip = styled(RowAligned)`
  flex-wrap: wrap;
  gap: 8px 24px;
`

const DateSegment = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const ResultsAnalyticsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`

const DistributionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  // Add breathing room between the 9-grid and the statistics strips above.
  margin-top: 16px;
`

// Title on the left, heatmap/scatter toggle on the right, above the 9-grid.
const DistributionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const DetailViewToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  // The detailed scatter view isn't available on mobile (forced to the
  // simplified heatmap), so hide the toggle there.
  ${mqTo.M`
    display: none;
  `}
`

const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
`

const SelfResultsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.textLighter};
  background: ${({ theme }) => theme.colors.lightest};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }

  > span:first-child {
    margin-right: 0;
  }

  /* Hide on mobile: the table collapses to name + details only, so self-assessment scores are not shown anyway */
  ${mqTo.M`
    display: none;
  `}
`

const InfoCards = styled(Row)`
  ${mqFrom.M`
    align-items: stretch;
  `}
`

const InfoCard = styled.div<{
  warn?: boolean
  resultsCard?: boolean
  compact?: boolean
}>`
  border-radius: 12px;
  padding: 16px;
  background-color: ${({ theme, warn, resultsCard }) =>
    warn
      ? theme.colors.secondaryFade
      : resultsCard
      ? theme.colors.primaryFadeLight
      : theme.colors.textLightest};

  ${({ compact }) =>
    compact &&
    css`
      display: flex;
      align-items: center;
      gap: 12px;
    `}

  ${({ resultsCard }) =>
    resultsCard &&
    css`
      display: flex;
      flex-direction: column;
      justify-content: center;
    `}

  &:nth-of-type(2) {
    margin-top: 12px;
  }

  ${mqFrom.M`
    flex: 1;

    &:nth-of-type(2) {
      margin: 0 0 0 24px;
    }
  `}
`

const StyledIcon = styled(Icon)`
  margin-right: 12px;
`

const RowAlignedBorder = styled(RowAligned)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.text72};
  padding-bottom: 12px;
`

const StyledButton = styled.div`
  margin-right: 8px;

  button {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const StyledTag = styled(StatusTag)`
  text-transform: initial;
  display: flex;
  align-items: center;
`

const HoverContainer = styled(JobTag)`
  padding: 0;
  border-radius: 50px;
  background: ${({ theme }) => theme.colors.primaryLight};
`

export const BorderCard = styled.div`
  height: calc(100% - 24px);
  border: 1px solid ${({ theme }) => theme.colors.textLighter};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;

  & > div:nth-child(2) {
    margin: 24px 0;
  }
`

const StatsGridRow = styled(GridRow)`
  row-gap: 24px;

  > *:nth-child(1) {
    padding-right: 12px;
  }

  > *:nth-child(2) {
    padding-left: 12px;
  }

  ${BorderCard} {
    height: 100%;
    margin-bottom: 0;
  }
`

const ToolbarButton = styled(Button)<{ alignEnd?: boolean }>`
  height: 48px;

  ${({ alignEnd }) =>
    alignEnd &&
    `
    align-self: flex-end;
    margin-bottom: 2px; // to compensate for the border of the dropdowns
  `}
`
